import { FC, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { Spinner } from '@/shared/ui';

import { authLib, authModel } from '@/entities/auth';
import { userModel } from '@/entities/user';

import { useLogout } from '@/features/logout';
import { ForcedMFAEnrollment, useMFAGate } from '@/features/mfa';
import { usePresignBootstrap } from '@/features/presignAuth';
import { UploadAssetProvider } from '@/features/uploadAsset';

import { ChatWorkspace } from '@/widgets/AgentChat';
import { FDADentalProfessionalConfirmationModal } from '@/widgets/FDADentalProfessionalConfirmationModal';
import { RecordingToast } from '@/widgets/RecordingToast';
import {
  ContinueRecordingModal,
  TranscriptorRecordingModal,
} from '@/widgets/TranscriptorRecordingModal';

import { useFDADentalProfessionalConfirmation } from '@/app/hooks/useFDADentalProfessionalConfirmation';
import { useUserDataStream } from '@/app/hooks/useUserDataStream';

import { useTranscriptorCatalogScope } from '../hooks';
import { useAccountInfoStream } from '../hooks/useAccountInfoStream';
import { useOrganizationSettingsStream } from '../hooks/useOrganizationSettingsStream';
import { UploadAssetStatus } from '../ui/UploadAssetStatus/UploadAssetStatus';

import { AgentChatRoutes } from './AgentChatRoutes';
import styles from './ProtectedRoutes.module.scss';

export const ProtectedRoutes: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // NOTE: pathless layout route — useParams() does not see the child route's
  // :token.
  const invitationToken = useMatch(PATHS.organization_invite)?.params.token;

  useUserDataStream(invitationToken);
  useAccountInfoStream();
  useOrganizationSettingsStream();
  useFDADentalProfessionalConfirmation();
  usePresignBootstrap();
  useTranscriptorCatalogScope();

  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const isReportLimitedSession = useAppSelector(
    authModel.selectors.selectIsReportLimitedSession,
  );
  const limitedReportId = useAppSelector(
    authModel.selectors.selectLimitedReportId,
  );

  useEffect(() => {
    if (
      isReportLimitedSession &&
      limitedReportId &&
      authLib.isValidReportId(limitedReportId!) &&
      currentUserID
    ) {
      const currentPath = location.pathname;
      const allowedReportPath = `/reports/${limitedReportId}`;

      const isAllowedPath =
        currentPath === allowedReportPath ||
        currentPath.startsWith(`${allowedReportPath}/`) ||
        currentPath === '/';

      if (!isAllowedPath && currentPath !== allowedReportPath) {
        navigate(allowedReportPath, { replace: true });
      }
    }
  }, [
    isReportLimitedSession,
    limitedReportId,
    location.pathname,
    navigate,
    currentUserID,
  ]);

  const userLoading = useAppSelector(userModel.selectors.selectLoading);

  const userIsLoading = userLoading === 'pending' || userLoading === 'idle';

  const {
    isEnrollmentRequired,
    isVerificationRequired,
    requiredByOrganizationName,
    completeEnrollment,
  } = useMFAGate();
  const logout = useLogout();

  if (!currentUserID) {
    if (userIsLoading) {
      return (
        <div className={styles.spinnerWrapper}>
          <Spinner className={styles.spinner} size={96} />
        </div>
      );
    }
    return <Navigate replace to={PATHS.signIn} />;
  }

  if (isEnrollmentRequired || isVerificationRequired) {
    return (
      <ForcedMFAEnrollment
        isVerificationRequired={isVerificationRequired}
        requiredByOrganizationName={requiredByOrganizationName}
        onSignOut={logout}
        onCompleted={completeEnrollment}
      />
    );
  }

  return (
    <UploadAssetProvider>
      {/* A background user/org stream refresh must never unmount the page:
          the user-data stream restarts on transient network errors, and each
          restart flips the loading state to pending. Showing the spinner here
          would unmount the whole app (including any open agent chat) every
          reconnect. The spinner is only meaningful before a user exists. */}
      <ChatWorkspace>
        <Outlet />
        <AgentChatRoutes />
      </ChatWorkspace>
      <RecordingToast />
      <FDADentalProfessionalConfirmationModal />
      <TranscriptorRecordingModal />
      <ContinueRecordingModal />
      <UploadAssetStatus />
    </UploadAssetProvider>
  );
};
